import { gatewayApi as api } from './gatewayApi';

// Types
export interface User {
	id: string;
	username: string;
	email: string;
	firstName: string;
	lastName: string;
	role: string;
	tenant: string;
	isActive: boolean;
	lastLogin?: string;
}

export interface LoginCredentials {
	username: string;
	password: string;
	companyCode: string;
}

export interface LoginResponse {
	user: User;
	message: string;
	success: boolean;
}

export interface ChangePasswordRequest {
	currentPassword: string;
	newPassword: string;
	confirmPassword: string;
}

function normalizeUser(raw: any): User {
	const d = raw || {};
	return {
		id: String(d.Id ?? d.id ?? ''),
		username: String(d.Username ?? d.username ?? d.Email ?? d.email ?? ''),
		email: String(d.Email ?? d.email ?? ''),
		firstName: String(d.FirstName ?? d.firstName ?? ''),
		lastName: String(d.LastName ?? d.lastName ?? ''),
		role: String((d.Role && (d.Role.Name ?? d.Role.role ?? d.Role)) ?? d.role ?? ''),
		tenant: String(d.Tenant ?? d.tenant ?? ''),
		isActive: Boolean(d.Active ?? d.isActive ?? true),
		lastLogin: d.LastLogin ?? d.lastLogin,
	};
}

function normalizeLogin(raw: any): LoginResponse {
	// Support shapes: { success, user, message } OR { data: {..., jwt} } OR flat user with jwt
	const container = raw?.data ? raw : { data: raw };
	const payload = container.data || {};
	const token = payload.jwt ?? raw?.jwt ?? payload.token ?? raw?.token;
	if (token) {
		try { localStorage.setItem('jwt', String(token)); } catch { /* ignore */ }
	}
	return {
		success: Boolean(raw?.success ?? true),
		message: String(raw?.message ?? ''),
		user: normalizeUser(payload),
	};
}

// Authentication API Service
export const authApi = {
	// Login
	login: (credentials: LoginCredentials): Promise<LoginResponse> => {
		const payload = {
			username: credentials.username,
			password: credentials.password,
			companycode: credentials.companyCode, // backend expects lowercase companycode
		};
		return api.post<any>('/auth/signin', payload).then(normalizeLogin);
	},

	// Logout
	logout: (): Promise<void> =>
		api.post<void>('/auth/logout'),

	// Get current user
	getCurrentUser: (): Promise<User> =>
		api.get<User>('/user'),

	// Check if user is authenticated
	isAuthenticated: (): Promise<boolean> =>
		api.get<boolean>('/auth/check'),

	// Change password
	changePassword: (request: ChangePasswordRequest): Promise<{ message: string }> =>
		api.put<{ message: string }>('/auth/password', request),

	// Reset password
	resetPassword: (email: string): Promise<{ message: string }> =>
		api.post<{ message: string }>('/auth/password-reset', { email }),

	// Verify reset token
	verifyResetToken: (token: string): Promise<{ valid: boolean }> =>
		api.get<{ valid: boolean }>(`/auth/password-reset/${token}`),

	// Set new password with reset token
	setNewPassword: (token: string, newPassword: string): Promise<{ message: string }> =>
		api.post<{ message: string }>(`/auth/password-reset/${token}`, { newPassword }),

	// Refresh session
	refreshSession: (): Promise<{ user: User }> =>
		api.post<{ user: User }>('/auth/refresh'),
}; 